import { ApiError, failureOf } from "../lib/api";
import type { Account } from "../hub/api";

export type PlatformAccount = Account & { role?: "owner" | "admin" };

export interface Publication {
  id: string;
  agent_id: string;
  agent_name: string;
  title: string;
  description: string;
  action_label: string;
  version: string;
  created_at: number;
  featured: boolean;
  available: boolean;
  status: "published" | "withdrawn" | "suspended" | "draft";
  cover_url?: string;
  entry?: string;
  preview?: boolean;
  actions?: { id: string; label: string }[];
  /** How many viewers like it, whether the one reading it does, and whether it is their own work. */
  likes?: number;
  liked?: boolean;
  mine?: boolean;
  /** In the owner's own list only: what Square saw of it. */
  numbers?: { viewers: number; finished: number; likes: number };
  /** Owner-only provenance of the frozen bytes; never supplied by public endpoints. */
  provenance?: { panel_key: string; entry: string; entry_sha256: string; source_revision: string | null };
}

export interface PublicAgent {
  id: string;
  name: string;
  description?: string;
  publication_count: number;
}

export interface PublicationCandidate {
  agent_id: string;
  agent_name: string;
  panel_key: string;
  /** The exact logical entry currently offered by this panel, before a snapshot is prepared. */
  entry?: string;
  title: string;
  description: string;
  action_label: string;
  /** The publication this exact work already is; it is not pending. */
  published?: string;
  /** Where its declared cover is served from, when it declares one. */
  cover_url?: string;
}

export interface AdminOverview {
  readiness?: { issues: { title: string; detail: string; href: string; action: string }[]; active_services: number; chat_model: string | null };
  accounts: { total: number; disabled: number; admins: number };
  agents: { total: number; online: number; cloud: number; local: number; desired_running: number };
  invites: { unused: number };
  usage: { available: boolean; reason: string };
}

export interface AdminAccount {
  id: string;
  name: string;
  created_at: number;
  role: "owner" | "admin";
  disabled: boolean;
  max_agents: number | null;
  agent_count: number;
  session_count: number;
  /** Whether a confirmed email is on the account, so a forgotten password can be recovered by mail. */
  email_verified: boolean;
}

/** The mail service as the admin page reads it; the password is only ever said to be there. */
export interface MailView {
  mail: { host: string; port: number; tls: string; user: string; sender: string; password_present: boolean };
  configured: boolean;
  ready: boolean;
  link_base: string;
  tls_modes: { value: string; title: string; port: number }[];
}

export interface AdminAgent {
  id: string;
  name: string;
  owner: string;
  owner_name: string;
  location: "cloud" | "local";
  online: boolean;
  desired_running?: boolean;
  runtime?: import("../lib/api").HubAgent["runtime"];
  runner?: import("../lib/api").HubAgent["runner"];
  /** Whether the platform expects it connected: a platform-run Agent nobody stopped. */
  should_run?: boolean;
  /** Whether a local Agent was ever paired with a machine. */
  paired?: boolean;
  created_at: number;
}

export interface Invite {
  id: string;
  note: string;
  created_at: number;
  used_by: string | null;
  used_at: number | null;
}

export interface AuditEntry {
  id: string;
  actor_id: string;
  actor_name: string;
  action: string;
  target_id: string;
  /** The account or Agent the action was on; null for anything else. */
  target_name: string | null;
  reason: string;
  details: Record<string, unknown>;
  created_at: number;
}

/** One setting the Hub declares for the whole platform, with the value it holds now. */
export interface PlatformSetting {
  key: string;
  title: string;
  type: "integer" | "decimal";
  unit: string;
  note: string;
  default: number;
  minimum: number;
  maximum: number;
  step: number;
  value: number;
}

export interface PlatformSettingGroup {
  section?: "runtime" | "credits";
  key: string;
  title: string;
  note: string;
  settings: PlatformSetting[];
}

/**
 * An outside application the platform registers once for every owner (a plugin declares it).
 * A secret field says only that it is set; its value never comes back.
 */
export interface PlatformCredential {
  key: string;
  title: string;
  note: string;
  /** Where in the outside service's settings the return address is registered, as the plugin worded it; empty says nothing. */
  callback_note: string;
  /** Whether every field holds a value: half an application is none. */
  ready: boolean;
  fields: { key: string; title: string; shown: boolean; present: boolean; value?: string }[];
  /** Where the outside service is told to send the owner back; empty when the Hub's own address is not usable. */
  callbacks: string[];
}

/** Platform pages use only public/ownership APIs, never an Agent's private relay. */
export async function platformRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: "same-origin",
      headers: { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}) },
    });
  } catch (error) {
    if (init.signal?.aborted) throw error;
    throw new ApiError("连接暂时中断", "还没有收到服务器的结果。", "请重新读取状态后再操作。", 0);
  }
  let body: unknown;
  try { body = await response.json(); }
  catch { throw new ApiError("没有读到完整结果", "服务器没有返回可读取的内容。", "请稍后重新读取。", response.status); }
  if (!response.ok) {
    if (response.status === 401 && path.startsWith("/api/admin/") && !["/api/admin/login", "/api/admin/password/reset", "/api/admin/password/check-code"].includes(path))
      window.dispatchEvent(new Event("pa:admin:expired"));
    throw failureOf(response.status, body, { title: "请求未完成", detail: `服务器返回 ${response.status}。`, machine: false });
  }
  return body as T;
}

export function platformPost<T>(path: string, body: unknown): Promise<T> {
  return platformRequest<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export const workHref = (id: string) => `/#/work/${encodeURIComponent(id)}`;
export const channelHref = (id: string) => `/#/channel/${encodeURIComponent(id)}`;

let visitorTurn: Promise<unknown> = Promise.resolve();
/**
 * Run a request that may make this browser a Square visitor, one at a time.
 *
 * The first such answer may set the HttpOnly visitor cookie. Finishing it before
 * the next request starts keeps one browser one visitor, in every tab.
 */
export function asVisitor<T>(request: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  if (navigator.locks) return navigator.locks.request("pa-public-visitor", signal ? { signal } : {}, request);
  const result = visitorTurn.then(request);
  visitorTurn = result.then(() => {}, () => {});
  return result;
}

/** What a player tells Square at a stop: how far the viewer got, the film's length, whether it reached the end. */
export type Watched = { at: number; length: number; ended: boolean };

/**
 * Tell Square this viewer watched a film this far, or opened a page (no `watched`).
 *
 * `leaving`: the page is going away, so the request goes now rather than wait its
 * turn. A report that does not arrive is a viewing not counted, never a problem
 * on screen.
 */
export function reportViewing(id: string, watched?: Watched, leaving = false): void {
  const send = async () => {
    await fetch(`/api/public/works/${encodeURIComponent(id)}/views`, {
      method: "POST",
      keepalive: true,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(watched ?? {}),
    });
  };
  (leaving ? send() : asVisitor(send)).catch(() => {});
}

/** Give this viewer's like, or take it back; the answer is the count and where the viewer stands. */
export function likeWork(id: string, liked: boolean): Promise<{ likes: number; liked: boolean }> {
  return asVisitor(() => platformRequest(`/api/public/works/${encodeURIComponent(id)}/like`, {
    method: liked ? "PUT" : "DELETE",
  }));
}

export function publicationState(work: Publication): string {
  if (work.status === "withdrawn") return "已撤回";
  if (work.status === "suspended" || work.available === false) return "已暂停公开";
  return "已公开";
}

// To the minute: two versions of one work published the same day stay apart.
export function publishedDate(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "时间未提供";
  return new Date(value * 1000).toLocaleString("zh-CN", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

const publicationChanges = new EventTarget();

// A local publishing decision invalidates projections, including a hidden Square.
export function onPublicationsChanged(listener: () => void): () => void {
  publicationChanges.addEventListener("change", listener);
  return () => publicationChanges.removeEventListener("change", listener);
}

function publicationPost<T>(path: string, body: unknown): Promise<T> {
  // A lost reply may have changed visibility too. Readers ask the existing API
  // for the fact; this notification never guesses whether the command worked.
  return platformPost<T>(path, body).finally(() => publicationChanges.dispatchEvent(new Event("change")));
}

export const publicationsApi = {
  own: "/api/hub/publications",
  candidates: "/api/hub/publication-candidates",
  prepare: (candidate: PublicationCandidate) => platformPost<{ publication: Publication; preview_asset_base: string; preview_expires_at: number }>("/api/hub/publication-previews",
    { agent_id: candidate.agent_id, panel_key: candidate.panel_key }),
  publish: (preview: Publication) => publicationPost<{ publication: Publication }>("/api/hub/publications",
    { preview_id: preview.id, version: preview.version }),
  visibility: (work: Publication, action: "withdraw" | "restore") =>
    publicationPost<{ publication: Publication }>(`/api/hub/publications/${encodeURIComponent(work.id)}/${action}`, {}),
  moderate: (work: Publication, changes: { available: boolean; featured: boolean; reason: string }) =>
    publicationPost(`/api/admin/publications/${encodeURIComponent(work.id)}`, changes),
};
